import type { GetFormCustomisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

type Element = {
  key: string;
  value: ReactElement;
};

type FieldOrdering = {
  FieldId: string;
  Position: number;
};

export interface SectionConfig {
  SectionId: string;
  Label: string;
  Description?: string;
  fields: string[];
}

interface SectionManagerReturn {
  sections: SectionConfig[];
  draggedFieldId: string | null;
  handleFieldReorderInSection: (
    sectionId: string,
    newFieldOrder: string[]
  ) => void;
  setDraggedFieldId: (fieldId: string | null) => void;
}

/**
 * Manages section state and field grouping for customizable forms.
 * Creates a default "Details" section if no sections are configured.
 */
export const useSectionManager = (
  elementsByKey: Element[],
  formSections: GetFormCustomisationQuery['form_section_configuration'] | null,
  formFieldOrdering?: FieldOrdering[]
): SectionManagerReturn => {
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);

  const orderFieldsByPosition = useCallback(
    (fieldIds: string[]): string[] => {
      if (!formFieldOrdering) {
        return fieldIds;
      }

      return [...fieldIds].sort((a, b) => {
        const aPos = formFieldOrdering.find((f) => f.FieldId === a)?.Position;
        const bPos = formFieldOrdering.find((f) => f.FieldId === b)?.Position;

        if (aPos === undefined) {
          return 1;
        }
        if (bPos === undefined) {
          return -1;
        }

        return aPos - bPos;
      });
    },
    [formFieldOrdering]
  );

  const initializeSections = useCallback((): SectionConfig[] => {
    const allFieldIds = elementsByKey.map((e) => e.key);

    if (!formSections || formSections.length === 0) {
      return [
        {
          SectionId: 'section_details',
          Label: 'Details',
          Description: '',
          fields: orderFieldsByPosition(allFieldIds),
        },
      ];
    }

    const sortedFormSections = [...formSections].sort(
      (a, b) => (a.ordering?.Position ?? 0) - (b.ordering?.Position ?? 0)
    );

    const sections = sortedFormSections.map((section) => {
      const sectionFieldIds = section.fields?.map((f) => f.FieldId) ?? [];

      return {
        SectionId: section.SectionId,
        Label: section.Label ?? section.SectionId,
        Description: section.Description ?? '',
        fields: orderFieldsByPosition(sectionFieldIds),
      };
    });

    // Custom fields can be created without yet being saved to form_section_field;
    // park them at the end of the last section so they still render.
    const mappedFieldIds = new Set(
      sections.flatMap((section) => section.fields)
    );
    const unmappedFieldIds = allFieldIds.filter(
      (fieldId) => !mappedFieldIds.has(fieldId)
    );

    if (unmappedFieldIds.length > 0 && sections.length > 0) {
      const lastSection = sections[sections.length - 1];
      lastSection.fields.push(...unmappedFieldIds);
    }

    return sections;
  }, [elementsByKey, formSections, orderFieldsByPosition]);

  const [sections, setSections] = useState<SectionConfig[]>(initializeSections);

  useEffect(() => {
    const newSections = initializeSections();
    if (!_.isEqual(newSections, sections)) {
      setSections(newSections);
    }
    // initializeSections closes over elementsByKey, a new array reference on
    // every render, so including it (or the function itself) would re-run this
    // effect every render. elementsByKey.length is the stable signal we care
    // about (field added/removed). sections is read only by the deep-equality
    // guard above, which exists to break the setSections feedback loop — adding
    // it as a dep would defeat that guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSections, elementsByKey.length, formFieldOrdering]);

  const handleFieldReorderInSection = useCallback(
    (sectionId: string, newFieldOrder: string[]) => {
      setSections((prevSections) =>
        prevSections.map((section) =>
          section.SectionId === sectionId
            ? { ...section, fields: newFieldOrder }
            : section
        )
      );
    },
    []
  );

  return {
    sections,
    draggedFieldId,
    handleFieldReorderInSection,
    setDraggedFieldId,
  };
};
